import React, { useEffect, useState } from 'react';
import './index.css';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import CookieConsent from './components/cookieConsent';
import AdminLogin from './components/auth/AdminLogin';
import AcceptInvitePage from './components/auth/AcceptInvitePage';
import AdminShell from './components/admin/AdminShell';
import EditWebsitePage from './components/adminPanel/EditWebsitePage';
import ContentItemEditorPage from './components/adminPanel/ContentItemEditorPage';
import Settings from './components/adminPanel/Settings';

import GDPR from './pages/legal/GDPR';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import CookiePolicy from './pages/legal/CookiePolicy';
import FAQ from './pages/help/FAQ';

import PublicHeaderSimple from './components/public/PublicHeader';
import AdminFooter from './components/layout/AdminFooter';


import Changelog from './pages/public/Changelog';
import Status from './pages/public/Status';
import Guides from './pages/public/Guides';
import CookieSettings from './pages/public/CookieSettings';

import { api } from './lib/api';
import { AuthContext } from './contexts/AuthContext';
import { SiteContext } from './contexts/SiteContext'; // exported for children that import it
import RequireAuth from './components/auth/RequireAuth';

export { SiteContext }; // so children can import from 'App' if you prefer

function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <PublicHeaderSimple />
      <main className="flex-1">
        <Outlet />
      </main>
      <AdminFooter
        // use public (root) links on the login page
        faqUrl="/faq"
        gdprUrl="/gdpr"
        privacyUrl="/privacy"
        termsUrl="/terms"
        cookiesUrl="/cookies"
        onManageCookies={() => window.dispatchEvent(new CustomEvent('cookie:open'))}
      />
    </div>
  );
}

export default function App() {
  const { t } = useTranslation();
  const [user, setUser] = useState({ auth: false, name: '', websites: [] });

  // language by IP
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        i18n.changeLanguage(data.country === 'SE' ? 'se' : 'en');
      } catch {
        i18n.changeLanguage('en');
      }
    })();
  }, []);

  // persist auth across refresh
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/me`);
        if (data?.username) {
          setUser({ auth: true, name: data.username, websites: data.websites || [], username: data.username });
        }
      } catch { /* not logged in */ }
    })();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <div className="App">
        <CookieConsent t={t}/>
        <Router>
          <Routes>
            {/* Login */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<AdminLogin setAuthenticatedUser={setUser} t={t} />} />
              <Route path="/accept-invite" element={<AcceptInvitePage t={t}/>} />
              <Route path="/faq" element={<FAQ t={t}/>} />
              <Route path="/gdpr" element={<GDPR t={t}/>} />
              <Route path="/privacy" element={<PrivacyPolicy t={t}/>} />
              <Route path="/terms" element={<TermsOfService t={t}/>} />
              <Route path="/cookies" element={<CookiePolicy t={t}/>} />
              <Route path="/cookie-settings" element={<CookieSettings t={t}/>} />
              <Route path="/changelog" element={<Changelog t={t}/>} />
              <Route path="/status" element={<Status t={t}/>} />
              <Route path="/guides" element={<Guides t={t}/>} />
            </Route>

            {/* Per-site shell + nested admin routes */}
            <Route
              path="/:username/:website"
              element={
                <RequireAuth user={user}>
                  <AdminShell t={t}/>
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="edit" replace />} />
+             <Route path="edit" element={<EditWebsitePage t={t}/>}>
                {/* keep all “section” routes nested so the sidebar persists */}
                <Route path=":sectionSlug/item" element={<ContentItemEditorPage t={t}/>} />
                <Route path=":sectionSlug/item/:itemSlug" element={<ContentItemEditorPage t={t}/>} />
              </Route>
              <Route path="settings" element={<Settings t={t}/>} />
            </Route>

            <Route path="*" element={<div className="p-6">{t('errors.notFound')}</div>} />
          </Routes>
        </Router>
      </div>
    </AuthContext.Provider>
  );
}
